import React, { useState } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Form, InputGroup, ProgressBar } from 'react-bootstrap';
import { MdSearch, MdAdd, MdEdit, MdDelete, MdInventory, MdWarning } from 'react-icons/md';

const Inventory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const inventory = [
    { id: 'INV-001', name: 'Laptop Pro 15"', category: 'Electronics', stock: 45, minStock: 10, price: 1299.99, status: 'In Stock' },
    { id: 'INV-002', name: 'Wireless Mouse', category: 'Accessories', stock: 3, minStock: 5, price: 29.99, status: 'Low Stock' },
    { id: 'INV-003', name: 'USB-C Cable', category: 'Cables', stock: 0, minStock: 20, price: 19.99, status: 'Out of Stock' },
    { id: 'INV-004', name: 'Bluetooth Headphones', category: 'Audio', stock: 12, minStock: 8, price: 199.99, status: 'In Stock' },
    { id: 'INV-005', name: 'Gaming Keyboard', category: 'Gaming', stock: 7, minStock: 5, price: 149.99, status: 'Low Stock' }
  ];

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      'In Stock': 'success',
      'Low Stock': 'warning',
      'Out of Stock': 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getStockPercentage = (stock: number, minStock: number) => {
    return Math.min((stock / (minStock * 2)) * 100, 100);
  };

  const filteredInventory = inventory.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container fluid className="py-4">
      <div className="page-header mb-4">
        <h2><MdInventory className="me-2" />Stock Management System</h2>
        <p className="text-muted">Track and manage your product inventory</p>
      </div>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Inventory Items</h5>
              <Button variant="primary">
                <MdAdd className="me-1" />
                Add Item
              </Button>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <MdSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search inventory..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>

              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Item ID</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Stock</th>
                    <th>Min Stock</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.name}</td>
                      <td>{item.category}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="me-2">{item.stock}</span>
                          <ProgressBar 
                            now={getStockPercentage(item.stock, item.minStock)} 
                            variant={item.stock === 0 ? 'danger' : item.stock <= item.minStock ? 'warning' : 'success'}
                            style={{ width: '60px', height: '8px' }}
                          />
                        </div>
                      </td>
                      <td>{item.minStock}</td>
                      <td>${item.price}</td>
                      <td>{getStatusBadge(item.status)}</td>
                      <td>
                        <Button variant="outline-secondary" size="sm" className="me-1">
                          <MdEdit />
                        </Button>
                        <Button variant="outline-danger" size="sm">
                          <MdDelete />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">156</h3>
              <p className="text-muted mb-0">Total Items</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">134</h3>
              <p className="text-muted mb-0">In Stock</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">18</h3>
              <p className="text-muted mb-0">Low Stock</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-danger">4</h3>
              <p className="text-muted mb-0">Out of Stock</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Header>
              <h6 className="mb-0">
                <MdWarning className="me-2" />
                Low Stock Alerts
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="alert-list">
                {inventory.filter(item => item.stock <= item.minStock).map((item) => (
                  <div key={item.id} className="alert alert-warning d-flex justify-content-between align-items-center">
                    <span>
                      <strong>{item.name}</strong> - Only {item.stock} left (Min: {item.minStock})
                    </span>
                    <Button variant="outline-primary" size="sm">
                      Reorder
                    </Button>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Inventory;
